import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'

import TextAreaField from '../../../../FormLegacy/FormFieldsV2/TextAreaField'
import ToggleField from '../../../../FormLegacy/FormFieldsV2/ToggleField'
import Stepper from '../../../../Stepper/Stepper'
import API from '../../../../../api/api'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'
import ImagesPicker from '../../../../ImagesPicker/ImagesPicker'
import ScheduledDateTimeFields from '../../../ScheduledDateTimeFields/ScheduledDateTimeFields'
import { useScheduledDateTime } from '../../../ScheduledDateTimeFields/useScheduledDateTime'
import { ENTRY_NOTE_MAX_LENGTH } from '../../constants'

const INITIAL_FORM_STATE = {
  category: null,
  subcategory: null,
  briefDescription: '',
  description: '',
  priority: null,
  pets: false,
  permissionToEnter: true,
  entryNote: '',
  photos: [],
}
const YardiSettings = ({
  building,
  resident,
  assignee,
  phase,
  onBack,
  onCancel,
  voicemailChatMessageSid,
  defaultPhotos,
  inspectionId,
  description,
  onSuccess,
  checklistRoomItemId,
}) => {
  const { t } = useTranslation(['maintenance', 'common'])
  const scheduledDateTime = useScheduledDateTime({ timezone: building.timezone })
  const { displaySnackbar } = useSnackbar()
  const [formState, setFormState] = useState(INITIAL_FORM_STATE)
  const [yardiConfig, setYardiConfig] = useState({ categories: [], priorities: [] })
  const [formErrors, setFormErrors] = useState({})

  const handleFieldChange = ({ target: { value, name } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const handleEntryNoteChange = ({ target: { value, name } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))

    // Validate entry note length
    if (value.length > ENTRY_NOTE_MAX_LENGTH) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: t('maintenance:form.pmsSettings.entryNote.validation', {
          maxLength: ENTRY_NOTE_MAX_LENGTH,
        }),
      }))
    } else {
      setFormErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleToggleChange = ({ target: { name, checked } }) => {
    setFormState((prev) => ({ ...prev, [name]: checked }))
  }

  const handleSubmit = async () => {
    if (!scheduledDateTime.validate()) return

    const requestData = {
      buildingId: building.id,
      yardi: true,
      briefDescription: formState.briefDescription,
      description: formState.description,
      priority: formState.priority.value,
      enterPermission: formState.permissionToEnter,
      pets: formState.pets,
      entryNote: formState.entryNote,
      photos: formState.photos.map((photo, index) =>
        photo.file instanceof File
          ? photo.file
          : new File([photo.file], `photo-${index}.jpg`, { type: photo.file.type || 'image/jpeg' }),
      ),
      ...(formState.category && { category: formState.category.value }),
      ...(formState.subcategory && { subcategory: formState.subcategory.value }),
      ...(assignee && { assignee: assignee.id }),
      ...(scheduledDateTime.timestamp && { scheduledDate: scheduledDateTime.timestamp }),
    }
    if (resident.clientId) {
      requestData.clientId = resident.clientId
    } else if (resident.unit) {
      requestData.unit = resident.unit
    }
    if (resident.sourceUnitId) {
      requestData.unitId = resident.sourceUnitId
    }
    if (phase) {
      requestData.yardiPropertyId = phase
    }
    if (voicemailChatMessageSid) {
      requestData.voicemailChatMessageSid = voicemailChatMessageSid
    }
    if (checklistRoomItemId) {
      requestData.checklistRoomItemId = checklistRoomItemId
    } else if (inspectionId) {
      requestData.inspectionId = inspectionId
    }

    try {
      await API.createMaintenance(requestData)
      displaySnackbar(t('maintenance:form.pmsSettings.snackbar.created'))
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : t('maintenance:form.pmsSettings.snackbar.error')
      displaySnackbar(message, true)
    } finally {
      onCancel()
    }
  }

  const areAllFieldsValid =
    !!formState.briefDescription && !!formState.priority && Object.keys(formErrors).length === 0

  useEffect(() => {
    ;(async () => {
      const resp = await API.getYardiConfig(building.id)
      const categoriesOptions = resp.data.categories.map(({ Name, SubCategory }) => ({
        value: Name,
        label: Name,
        subcategories: SubCategory
          ? typeof SubCategory === 'string'
            ? [{ value: SubCategory, label: SubCategory }]
            : SubCategory.map((subcategory) => ({
                value: subcategory,
                label: subcategory,
              }))
          : [],
      }))
      const prioritiesOptions = resp.data.priorities.map((priority) => ({
        label: priority,
        value: priority,
      }))
      setYardiConfig({ categories: categoriesOptions, priorities: prioritiesOptions })
    })()
  }, [building, building.id])

  useEffect(() => {
    if (description) {
      setFormState((prev) => ({ ...prev, description }))
    }
  }, [description])

  // Note: Load default photos if these are passed
  useEffect(() => {
    if (!defaultPhotos || !defaultPhotos.length) return

    const loadDefaultPhotos = async () => {
      try {
        const preparedPhotos = await Promise.all(
          defaultPhotos.slice(0, 3).map(async (url, index) => {
            try {
              const response = await fetch(url)
              const blob = await response.blob()
              const file = new File([blob], `photo-${index}.jpg`, {
                type: blob.type || 'image/jpeg',
              })

              return {
                image: url,
                file,
              }
            } catch {
              return null
            }
          }),
        )

        const validPhotos = preparedPhotos.filter(Boolean)
        if (validPhotos.length) {
          setFormState((prev) => ({
            ...prev,
            photos: validPhotos,
          }))
        }
      } catch (error) {
        console.error(error)
      }
    }

    loadDefaultPhotos()
  }, [defaultPhotos])

  return (
    <div className="maintenance-request-modal">
      <Stepper
        steps={[
          t('maintenance:form.steps.requestDetails'),
          t('maintenance:form.steps.requestSettings'),
        ]}
        currentStep={1}
      />
      <div className="row">
        <div className="row-header">{t('maintenance:form.pmsSettings.selectCategory')}</div>
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          options={yardiConfig.categories}
          name="category"
          value={formState.category}
          onChange={(e) => setFormState((prev) => ({ ...prev, category: e }))}
          placeholder={t('maintenance:form.pmsSettings.selectCategory')}
        />
      </div>
      {formState.category && (
        <div className="row">
          <div className="row-header">{t('maintenance:form.yardi.selectSubcategory')}</div>
          <Select
            className="select-container"
            classNamePrefix="select-primary"
            options={formState.category.subcategories}
            name="subcategory"
            value={formState.subcategory}
            onChange={(e) => setFormState((prev) => ({ ...prev, subcategory: e }))}
            placeholder={t('maintenance:form.yardi.selectSubcategory')}
          />
        </div>
      )}
      <div className="row">
        <ScheduledDateTimeFields {...scheduledDateTime.fieldProps} />
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.yardi.shortDescription')}</div>
        <TextAreaField
          placeholder={t('maintenance:form.yardi.shortDescriptionPlaceholder')}
          name="briefDescription"
          value={formState.briefDescription}
          maxLength={34}
          onChange={handleFieldChange}
        />
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.pmsSettings.requestDescription')}</div>
        <TextAreaField
          placeholder={t('maintenance:form.pmsSettings.descriptionPlaceholder')}
          name="description"
          value={formState.description}
          maxLength={250}
          onChange={handleFieldChange}
        />
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.yardi.priority')}</div>
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          options={yardiConfig.priorities}
          name="priority"
          value={formState.priority}
          onChange={(e) => setFormState((prev) => ({ ...prev, priority: e }))}
          placeholder={t('maintenance:form.yardi.priorityPlaceholder')}
        />
      </div>
      <div className="row">
        <div className="row-header">
          {t('maintenance:form.pmsSettings.photosLabel')}{' '}
          <span>{t('maintenance:form.requestDetails.optional')}</span>
        </div>
        <ImagesPicker
          value={formState.photos}
          imageLimit={3}
          onChange={(images) => handleFieldChange({ target: { value: images, name: 'photos' } })}
        />
      </div>
      <div className="row checkboxes">
        <div className="input-container">
          <ToggleField
            value={formState.pets}
            name="pets"
            label={t('maintenance:form.pmsSettings.pets')}
            labelOnRight
            onChange={handleToggleChange}
          />
        </div>
        <div className="input-container">
          <ToggleField
            name="permissionToEnter"
            label={t('maintenance:form.pmsSettings.permissionToEnter')}
            labelOnRight
            onChange={handleToggleChange}
            value={formState.permissionToEnter}
          />
        </div>
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.pmsSettings.entryNote.title')}</div>
        <TextAreaField
          name="entryNote"
          placeholder={t('maintenance:form.pmsSettings.entryNote.placeholder')}
          value={formState.entryNote}
          onChange={handleEntryNoteChange}
          isError={!!formErrors.entryNote}
        />
        {formErrors.entryNote && <span className={'label--error'}>{formErrors.entryNote}</span>}
      </div>
      <div className="modal-footer">
        <div className="buttons">
          <button className="button button-gray" onClick={onBack}>
            {t('maintenance:form.pmsSettings.back')}
          </button>
          <button className="button button-gray" onClick={onCancel}>
            {t('common:actions.cancel')}
          </button>
          <button
            className="button button-primary"
            disabled={!areAllFieldsValid}
            onClick={handleSubmit}
          >
            {t('maintenance:form.pmsSettings.createRequest')}
          </button>
        </div>
      </div>
    </div>
  )
}

YardiSettings.propTypes = {
  building: PropTypes.object,
  resident: PropTypes.object,
  assignee: PropTypes.object,
  phase: PropTypes.string,
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
  voicemailChatMessageSid: PropTypes.bool,
  inspectionId: PropTypes.number,
  description: PropTypes.string,
  defaultPhotos: PropTypes.array,
  onSuccess: PropTypes.func,
  checklistRoomItemId: PropTypes.number,
}

export default YardiSettings
